import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { sortAndFormatAccessorials } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { accessorialSchema } from "~/shared/schemas/accessorial-schema.ts";
import type {
	AddressBookCreateBodyV2,
	AddressBookCreateResponseV2,
	AddressBookUpdateBodyV2,
} from "~/shared/schemas/address-book-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";

import { useAddressBook } from "../../../hooks/use-address-book.ts";

type GroupedAccessorials = ReturnType<typeof sortAndFormatAccessorials>;

type UseAddAddressModalReturn = {
	createAddressBookItem: (
		body: AddressBookCreateBodyV2,
	) => Promise<AddressBookCreateResponseV2>;
	updateAddressBookItem: (body: AddressBookUpdateBodyV2) => Promise<void>;
	isSubmitting: boolean;
	groupedAccessorials: GroupedAccessorials | null;
};

export const useAddAddressModal = (): UseAddAddressModalReturn => {
	const {
		createAddressBookItem,
		isCreating,
		updateAddressBookItem,
		isUpdating,
	} = useAddressBook();

	const { data: allAccessorials } = useQuery({
		// Distinct from the shared ["accessorials"] key, which is populated with a
		// pre-grouped object by shipment management's useGetAccessorials. Reusing
		// that key would feed a non-array into sortAndFormatAccessorials.
		queryKey: ["accessorials", "raw-list"],
		queryFn: async () => {
			const result = await getAndValidate(
				apiRoutes.ACCESSORIALS,
				accessorialSchema.array(),
			);
			return result.success ? result.data : [];
		},
		staleTime: 1000 * 60 * 60, // 1 hour
	});

	const groupedAccessorials = useMemo(
		() => (allAccessorials ? sortAndFormatAccessorials(allAccessorials) : null),
		[allAccessorials],
	);

	return {
		createAddressBookItem,
		updateAddressBookItem,
		isSubmitting: isCreating || isUpdating,
		groupedAccessorials,
	};
};
